/**
 * ThemePicker — booth-only atmosphere selector.
 *
 * Operators tap the "✦ Atmosphere · {label}" footer affordance on
 * the launcher to switch the kiosk's atmosphere for the night.
 * Five atmospheres ship with v1 (Rustic Evening / Botanical
 * Wedding / Glam Celebration / Elegant Ivory / Midnight Gold).
 *
 * Theme selection is sessionStorage-persistent (handled by
 * useBoothTheme) and is treated as venue config — the retune-room
 * long-press deliberately preserves it across nights.
 *
 * Behaviour preserved verbatim from the previous inline component.
 */
import React from 'react';
import { BOOTH_THEMES } from '../../lib/boothOSThemes';
import { SERIF, Eyebrow } from './boothShellAtoms';

const ThemePicker = ({ theme, activeId, onPick, onClose }) => (
  <div
    data-testid="booth-theme-picker"
    style={{
      minHeight: '100vh',
      padding: 'clamp(32px, 5vw, 60px) clamp(20px, 5vw, 60px)',
      animation: 'editorialFade .5s ease-out',
    }}
  >
    <header style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 28,
    }}>
      <button
        type="button"
        onClick={onClose}
        data-testid="booth-theme-picker-close"
        style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: theme.inkMuted, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 14, letterSpacing: '0.04em', padding: 8,
        }}
      >← Back</button>
      <Eyebrow theme={theme}>✦ CHOOSE THE BOOTH ATMOSPHERE</Eyebrow>
      <span style={{ width: 60 }} aria-hidden />
    </header>
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: 18, maxWidth: 1100, margin: '0 auto',
    }}>
      {Object.values(BOOTH_THEMES).map((t) => {
        const isActive = t.id === activeId;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onPick(t.id)}
            data-testid={`booth-theme-${t.id}`}
            style={{
              textAlign: 'left',
              padding: '22px 22px 24px',
              borderRadius: 14,
              background: `radial-gradient(220px 140px at 30% 20%, ${t.bgHi} 0%, ${t.bg} 100%)`,
              border: `1px solid ${isActive ? t.gold : t.hair}`,
              cursor: 'pointer',
              color: t.ink,
              transition: 'transform 220ms ease, border-color 220ms ease',
              boxShadow: isActive
                ? `0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 ${t.gold}33`
                : '0 14px 38px rgba(0,0,0,0.35)',
              // Composite each theme card on its own layer so the
              // shadow rasterises once; pointer-down transform sits
              // on top without dirtying the underlying paint.
              willChange: 'transform',
            }}
            onPointerDown={(e) => { e.currentTarget.style.transform = 'translateY(2px)'; }}
            onPointerUp={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
            onPointerLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 10,
            }}>
              <span style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase',
                color: t.gold,
              }}>{t.eyebrow}</span>
              {isActive && (
                <span style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 11, color: t.gold, letterSpacing: '0.12em',
                }}>· in use</span>
              )}
            </div>
            <h3 style={{
              margin: 0, fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(20px, 1.8vw, 24px)',
              color: t.ink, letterSpacing: '-0.005em',
            }}>{t.label}</h3>
            <p style={{
              margin: '10px 0 0', fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13.5, color: t.inkMuted, lineHeight: 1.5,
            }}>{t.description}</p>
            {/* Mini gold dot row — visual sample of the theme's accent */}
            <div style={{
              marginTop: 16, display: 'flex', gap: 6, alignItems: 'center',
            }}>
              {[t.gold, t.goldDeep, t.ink, t.polaroidFrame].map((c, i) => (
                <span key={i} style={{
                  width: 14, height: 14, borderRadius: '50%',
                  background: c, border: `1px solid ${t.hair}`,
                }} />
              ))}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

export default ThemePicker;
